import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WorkflowDraft } from '../store/workflow.state';
import { WorkflowStore } from '../store/workflow.store';
import { WORKFLOW_LAYOUT_VERSION } from '../store/workflow-layout';

/** Recovery marker written when a 401 interrupts an editing session. */
export interface WorkflowSessionRecoveryInfo {
  timestamp: string;
  workflowId: string;
  workflowName: string;
}

/**
 * Persists the workflow being edited to localStorage so the structure
 * survives page refreshes and re-auth redirects. Counterpart of
 * FormPersistenceService on the form-builder side; single-draft semantics —
 * one in-progress workflow per browser profile.
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowPersistenceService {
  private workflowStore = inject(WorkflowStore);
  private router = inject(Router);
  private readonly STORAGE_KEY = 'workflow-designer-current-workflow';
  private readonly WORKFLOW_ID_KEY = 'workflow-designer-current-workflow-id';
  private readonly SESSION_RECOVERY_KEY = 'workflow-designer-session-recovery';

  constructor() {
    // Leaving the workflow area is the end of the editing session: discard
    // the draft, any recovery marker, and the in-memory store so stale state
    // can't resurface on the next visit. Refreshes and 401 re-auth redirects
    // never fire NavigationEnd out of the area, so recovery still works.
    let previousUrl = this.router.url;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => {
        const wasInWorkflows = previousUrl.startsWith('/workflows');
        const isInWorkflows = e.urlAfterRedirects.startsWith('/workflows');
        previousUrl = e.urlAfterRedirects;
        if (wasInWorkflows && !isInWorkflows) {
          this.clearDraftAndRecovery();
          this.workflowStore.initializeWorkflow('');
        }
      });
  }

  /**
   * Snapshot the current store state (workflow, nodes, canvas positions) to
   * localStorage. No-op when nothing is being edited.
   */
  saveDraft(): void {
    const workflow = this.workflowStore.currentWorkflow();
    if (!workflow) {
      return;
    }
    try {
      const draft: WorkflowDraft = {
        workflow,
        nodes: this.workflowStore.nodes(),
        positions: this.workflowStore.positions(),
        layoutSlots: this.workflowStore.layoutSlots(),
        layoutVersion: WORKFLOW_LAYOUT_VERSION,
        layoutOrientation: this.workflowStore.orientation(),
        isPersisted: this.workflowStore.isPersisted(),
        persistedNodeIds: this.workflowStore.persistedNodeIds(),
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(draft));
      localStorage.setItem(this.WORKFLOW_ID_KEY, workflow.id);
    } catch (error) {
      console.error('Error saving workflow draft to localStorage:', error);
    }
  }

  /**
   * Load the stored draft. Returns null (and clears storage) when the stored
   * payload is missing or unreadable.
   */
  loadDraft(): WorkflowDraft | null {
    try {
      const draftStr = localStorage.getItem(this.STORAGE_KEY);
      if (!draftStr) {
        return null;
      }
      const draft = JSON.parse(draftStr) as WorkflowDraft;
      if (!draft.workflow?.id) {
        this.clearDraft();
        return null;
      }
      return {
        workflow: draft.workflow,
        nodes: draft.nodes ?? [],
        positions: draft.positions ?? {},
        layoutSlots: draft.layoutSlots,
        layoutVersion: draft.layoutVersion,
        layoutOrientation: draft.layoutOrientation,
        isPersisted: draft.isPersisted ?? false,
        persistedNodeIds: draft.persistedNodeIds,
        savedAt: draft.savedAt ?? new Date().toISOString()
      };
    } catch (error) {
      console.error('Error loading workflow draft from localStorage:', error);
      this.clearDraft();
      return null;
    }
  }

  hasDraft(): boolean {
    return localStorage.getItem(this.STORAGE_KEY) !== null;
  }

  getStoredWorkflowId(): string | null {
    return localStorage.getItem(this.WORKFLOW_ID_KEY);
  }

  /** Check if the stored draft belongs to the given workflow id. */
  isStoredWorkflowCurrent(workflowId: string): boolean {
    return this.getStoredWorkflowId() === workflowId;
  }

  clearDraft(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.WORKFLOW_ID_KEY);
  }

  /**
   * Mark the current draft for session recovery. Called when a session
   * timeout is detected (401) so the workflow can be re-opened after the
   * user logs back in. Drafts without meaningful content are not marked.
   */
  markForSessionRecovery(): void {
    const draft = this.loadDraft();
    if (!draft) {
      return;
    }
    const hasContent = draft.workflow.name?.trim() || draft.nodes.length > 0;
    if (!hasContent) {
      return;
    }
    try {
      const info: WorkflowSessionRecoveryInfo = {
        timestamp: new Date().toISOString(),
        workflowId: draft.workflow.id,
        workflowName: draft.workflow.name || 'Untitled Workflow'
      };
      localStorage.setItem(this.SESSION_RECOVERY_KEY, JSON.stringify(info));
    } catch (error) {
      console.error('Error marking workflow for session recovery:', error);
    }
  }

  /**
   * Recovery info for a draft interrupted by a session timeout, or null.
   * Self-heals when the flag is stale (draft no longer present) or corrupt.
   */
  getSessionRecoveryInfo(): WorkflowSessionRecoveryInfo | null {
    try {
      const recoveryData = localStorage.getItem(this.SESSION_RECOVERY_KEY);
      if (!recoveryData) {
        return null;
      }
      const info = JSON.parse(recoveryData) as WorkflowSessionRecoveryInfo;
      if (!this.hasDraft()) {
        this.clearSessionRecovery();
        return null;
      }
      return info;
    } catch (error) {
      console.error('Error reading workflow session recovery info:', error);
      this.clearSessionRecovery();
      return null;
    }
  }

  clearSessionRecovery(): void {
    localStorage.removeItem(this.SESSION_RECOVERY_KEY);
  }

  /** Discard both the draft and any pending recovery flag. */
  clearDraftAndRecovery(): void {
    this.clearDraft();
    this.clearSessionRecovery();
  }
}
